import {
    HxObject,
    HxObjectID,
    HxObjectType,
    HxView,
    HxViewMode,
    HxViewType,
    HxMessageHeader,
    HxAttachmentHeader,
    HxConversationHeader,
    HxConversation,
    HxCalendarData,
    HxCalendarGroupData,
    HxCategoryData,
    HxAppointmentHeader,
    HxRmsTemplate,
    HxContact,
    HxInboxRule,
    HxMipLabel,
    HxGroupMipLabel,
    HxGroup,
    HxIconData,
    HxMessageHeaderServerIdPair,
} from '@outlook/hxcore_web';
import { decodeUtf8OrString } from 'decode-utf8-array-hx';
import { setInboxViewHandle, getConversationHeaderIdMapContext } from './hxConversationHeaderIdMap';

export interface HxIdMappingInfo {
    readonly hxObjectId: HxObjectID;
    readonly serverId: string;
    readonly context?: string;
}

export function getHxIdMappingInfo(hxObject: HxObject): HxIdMappingInfo | null {
    let serverId: Uint8Array | null = null;
    let context;
    // todo: Can we get hx to improve their typing to get type safety
    // here without a manual type assertion?
    switch (hxObject.objectType) {
        case HxObjectType.HxView:
            const hxView = hxObject as HxView;
            if (hxView.type === HxViewType.Inbox_Other) {
                // Hx has two separate HxView's for inbox and inbox_other which
                // share the same server id.
                // We map the server id to inbox and explicitly look for the
                // inbox_other view as necessary in resolvers.
                return null;
            } else if (hxView.type === HxViewType.Inbox) {
                setInboxViewHandle(hxView.objectId.getHandle());
            }
            serverId = hxView.serverId;
            break;
        case HxObjectType.HxMessageHeader:
            serverId = (hxObject as HxMessageHeader).serverId;
            break;
        case HxObjectType.HxAttachmentHeader:
            serverId = (hxObject as HxAttachmentHeader).serverId;
            break;
        case HxObjectType.HxConversationHeader:
            const hxConversationHeader = hxObject as HxConversationHeader;
            serverId = hxConversationHeader.serverId;
            context = getConversationHeaderIdMapContext(hxConversationHeader);
            break;
        case HxObjectType.HxConversation:
            serverId = (hxObject as HxConversation).serverId;
            break;
        case HxObjectType.HxCalendarData:
            serverId = (hxObject as HxCalendarData).remoteServerId;
            break;
        case HxObjectType.HxCalendarGroupData:
            serverId = (hxObject as HxCalendarGroupData).serverId;
            break;
        case HxObjectType.HxCategoryData:
            serverId = (hxObject as HxCategoryData).serverId;
            break;
        case HxObjectType.HxAppointmentHeader:
            serverId = (hxObject as HxAppointmentHeader).serverId;
            break;
        case HxObjectType.HxContact:
            serverId = (hxObject as HxContact).serverId;
            break;
        case HxObjectType.HxInboxRule:
            serverId = (hxObject as HxInboxRule).serverId;
            break;
        case HxObjectType.HxMipLabel:
            serverId = (hxObject as HxMipLabel).serverId;
            break;
        case HxObjectType.HxGroupMipLabel:
            serverId = (hxObject as HxGroupMipLabel).serverId;
            break;
        case HxObjectType.HxGroup:
            serverId = (hxObject as HxGroup).groupId;
            break;
        case HxObjectType.HxRmsTemplate:
            serverId = (hxObject as HxRmsTemplate).templateId;
            break;
        case HxObjectType.HxIconData:
            serverId = (hxObject as HxIconData).iconServerId;
            break;
        default:
            return null;
    }

    return {
        hxObjectId: hxObject.objectId,
        serverId: decodeUtf8OrString(serverId),
        context,
    };
}

/**
 * Get extra associations between hx object ids and servetr ids based on the type of
 * the object. Extra associations are associations other than the one between an
 * object's own objectId and serverId.
 *
 * It is expected that the serverId can sometimes be null in offline scenarios, but the
 * Hx ID should always be set in each mapping info.
 */
export function getReferencedIds(hxObject: HxObject): HxIdMappingInfo[] | null {
    switch (hxObject.objectType) {
        case HxObjectType.HxMessageHeader: {
            const messageHeader = hxObject as HxMessageHeader;
            if (!messageHeader.classicGroupedConversationId.isNil()) {
                return [
                    {
                        hxObjectId: messageHeader.classicGroupedConversationId,
                        serverId: decodeUtf8OrString(
                            messageHeader.classicGroupedConversationServerId
                        ),
                    },
                ];
            } else {
                return null;
            }
        }

        case HxObjectType.HxConversationHeader:
            const conversationHeader = hxObject as HxConversationHeader;
            const refs: HxIdMappingInfo[] = [];
            if (conversationHeader.conversationViewMode !== HxViewMode.ClassicMessages) {
                // Also ensure the hx conversation object is mapped.
                // It has the same server id (but no disambiguating context)
                refs.push({
                    hxObjectId: conversationHeader.conversationId,
                    serverId: decodeUtf8OrString(conversationHeader.serverId),
                });
            }
            addIdPairToMappingInfoArray(refs, conversationHeader.latestMessageHeader);
            addIdPairToMappingInfoArray(refs, conversationHeader.latestDraft);
            addIdPairToMappingInfoArray(refs, conversationHeader.latestMeetingMessageHeader);
            return refs;
    }

    return null;
}

function addIdPairToMappingInfoArray(
    mappingInfo: HxIdMappingInfo[],
    pair?: HxMessageHeaderServerIdPair
) {
    const hxObjectId = pair?.objectId;
    if (hxObjectId && !hxObjectId.isNil()) {
        mappingInfo.push({
            hxObjectId,
            serverId: decodeUtf8OrString(pair!.serverId),
        });
    }
}
